import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Images } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    api.get(`/projects/${id}`)
      .then(({ data }) => setProject(data))
      .catch(() => setNotFound(true));
  }, [id]);

  const backTo = project?.category === "commercial" ? "/commercial" : "/residential";

  return (
    <div data-testid="project-detail-page" className="bg-background min-h-screen">
      <Header />
      <main className="pt-28 pb-24 px-5 md:px-10 max-w-7xl mx-auto">
        {notFound && (
          <div className="py-24 text-center" data-testid="project-not-found">
            <h1 className="font-display text-5xl mb-4">PROJECT NOT FOUND</h1>
            <Link to="/" className="text-primary text-sm font-bold uppercase tracking-widest">Back to home</Link>
          </div>
        )}
        {!project && !notFound && <p className="text-white/40 text-sm py-24 text-center">Loading project...</p>}
        {project && (
          <>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              <Link to={backTo} data-testid="project-back-link" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-primary transition-colors duration-300 mb-8">
                <ArrowLeft size={15} aria-hidden="true" /> Back to {project.category} gallery
              </Link>
              <p className="text-primary text-xs font-bold tracking-[0.3em] uppercase mb-3">{project.category} · {project.photo_count} photo{project.photo_count === 1 ? "" : "s"}</p>
              <h1 className="font-display text-5xl md:text-8xl leading-[0.9] tracking-wide mb-14" data-testid="project-title">{project.title}</h1>
            </motion.div>
            {project.media.length === 0 ? (
              <div className="border border-dashed border-white/15 rounded-2xl py-20 flex flex-col items-center gap-3 text-white/40">
                <Images size={28} aria-hidden="true" />
                <p className="text-sm">Photos for this project are coming soon.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-5" data-testid="project-media-grid">
                {project.media.map((m, i) => (
                  <motion.figure
                    key={m.id}
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                    className={`overflow-hidden rounded-xl border border-white/10 ${i === 0 ? "sm:col-span-2 md:col-span-2" : ""}`}
                    data-testid={`project-media-${i}`}
                  >
                    {m.media_type === "video" ? (
                      <video src={mediaUrl(m.storage_path)} controls preload="metadata" className="w-full h-full object-cover" />
                    ) : (
                      <img src={mediaUrl(m.storage_path)} alt={`${project.title} — photo ${i + 1}`} loading="lazy" className={`w-full object-cover ${i === 0 ? "aspect-[16/9]" : "aspect-[4/3]"}`} />
                    )}
                  </motion.figure>
                ))}
              </div>
            )}
            <div className="mt-14 border-t border-white/10 pt-10">
              <Link to={backTo} data-testid="project-back-link-bottom" className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 text-xs font-bold uppercase tracking-widest hover:border-primary hover:text-primary transition-colors duration-300">
                <ArrowLeft size={15} aria-hidden="true" /> Choose another project
              </Link>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
